<template>
  <v-dialog :model-value="modelValue" max-width="500" @update:model-value="emit('update:modelValue', $event)">
    <v-card v-if="sourceCall" class="pa-4">
      <v-card-title class="retrigger__title">Call Again</v-card-title>
      <v-card-text>
        <p class="retrigger__info">
          Re-calling <strong class="retrigger__mono">{{ sourceCall.toPhoneNumber }}</strong> for the same patient and
          request. Previous call data will be preserved for comparison.
        </p>

        <v-radio-group v-model="sendMode" hide-details inline class="mb-2">
          <v-radio label="Send now" value="now" />
          <v-radio label="Schedule for later" value="later" />
        </v-radio-group>

        <v-text-field
          v-if="sendMode === 'later'"
          v-model="scheduledFor"
          type="datetime-local"
          :label="'When (' + tzLabel + ')'"
          variant="outlined"
          density="compact"
          hide-details />
      </v-card-text>

      <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mx-4 mb-2">{{ error }}</v-alert>

      <v-card-actions class="justify-end">
        <v-btn variant="text" size="small" @click="emit('update:modelValue', false)">Cancel</v-btn>
        <v-btn color="primary" size="small" :loading="loading" @click="submit">
          {{ sendMode === "now" ? "Call now" : "Schedule" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { zonedLocalToUtcIso } from "@/utils/dateUtils";

import { anchorFromCall, anchorPayload } from "@/components/voice_ai/anchors";
import { useVoiceAI } from "@/composables/useVoiceAI";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  sourceCall: { type: Object, default: null },
  // When provided, the tenant config comes from the parent and this dialog
  // fetches nothing on mount (LAM-4503).
  tenantConfig: { type: Object, default: undefined },
});
const emit = defineEmits(["update:modelValue", "triggered"]);

const { triggerCall, getConfig, loading } = useVoiceAI();
const fetchedConfig = ref(null);
const resolvedConfig = computed(() => (props.tenantConfig !== undefined ? props.tenantConfig : fetchedConfig.value));

const tzLabel = computed(() => {
  const tz = resolvedConfig.value?.timezone;
  if (!tz) return "your time";
  try {
    const fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" });
    const p = fmt.formatToParts(new Date()).find((p) => p.type === "timeZoneName");
    return p ? p.value : "your time";
  } catch {
    return "your time";
  }
});
const sendMode = ref("now");
const scheduledFor = ref("");
const error = ref("");

onMounted(async () => {
  if (props.tenantConfig !== undefined) return;
  try {
    fetchedConfig.value = await getConfig();
  } catch {
    // non-critical
  }
});

async function submit() {
  error.value = "";
  const src = props.sourceCall;
  if (!src) return;

  const anchor = anchorFromCall(src);
  if (!anchor) {
    error.value = "This call is not attached to a case";
    return;
  }
  const payload = {
    ...anchorPayload(anchor),
    templateKey: src.templateKey,
    toPhoneNumber: src.toPhoneNumber,
    variableValues: src.variableValues || {},
    parentCallSessionId: src.id,
  };
  if (sendMode.value === "later" && scheduledFor.value) {
    payload.scheduledFor = zonedLocalToUtcIso(scheduledFor.value, resolvedConfig.value?.timezone);
  }
  try {
    const session = await triggerCall(payload);
    emit("triggered", session);
  } catch (err) {
    error.value = err?.message || "Failed to start call";
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.retrigger__title {
  font-size: 16px !important;
  font-weight: 600;
  color: $lh_color_navy_dark;
}
.retrigger__info {
  font-size: 13px;
  color: $lh_color_navy_dark;
  margin-bottom: 12px;
  line-height: 1.5;
}
.retrigger__mono {
  font-family: "JetBrains Mono", monospace;
}
</style>
